import { supportedLocales } from '@/core/i18n'
import { RefundSlip, YMD } from '@/types'

export const removeLocaleFromPath = (path: string) => {
  for (const localeMap of supportedLocales) {
    if (path.startsWith('/' + localeMap.code)) {
      return path.replace('/' + localeMap.code, '')
    }
  }

  return path
}

export const masking = (len: number, str: string, mask: string = '*') => {
  if (str.length - 1 < Math.abs(len)) {
    return Array(str.length).fill(mask).join('')
  }

  if (len >= 0) {
    const unmasked = str.substring(len)
    const masked = Array(str.length - unmasked.length).fill(mask).join('')
    return masked + unmasked
  } else {
    const unmasked = str.substring(0, str.length + len)
    const masked = Array(str.length - unmasked.length).fill(mask).join('')
    return unmasked + masked
  }
}

export const autoMasking = (str: string, pos: 'left' | 'center' | 'right') => {
  let maskLength = Math.ceil(str.length * 0.3)
  if (str.length === 0) {
    return ''
  } else if (str.length === 1) {
    return '*'
  } else if (str.length === 2 || str.length === 3) {
    maskLength = 1
  }

  const stars = '*'.repeat(maskLength)
  if (pos === 'left') {
    return stars + str.slice(maskLength)
  } else if (pos === 'center') {
    const start = Math.floor((str.length - maskLength) / 2)
    const end = start + maskLength
    return str.slice(0, start) + stars + str.slice(end)
  } else if (pos === 'right') {
    return str.slice(0, str.length - maskLength) + stars
  }
}

export const formatDate = (input: Date | string, exp: 'yyyymmdd' | 'yymmdd', delimiter: string) => {
  const _exp = exp || 'yyyymmdd'
  const _delimiter = delimiter || '-'
  if (input instanceof Date) {
    const year = _exp === 'yyyymmdd' ? input.getFullYear() : input.getFullYear().toString().substring(2, 4)
    const month = input.getMonth() + 1
    const date = input.getDate()

    return [year, month, date].join(_delimiter)
  } else {
    if (input.length === 8) {
      return [input.substring(0, 4), input.substring(4, 6), input.substring(6, 8)].join(_delimiter)
    } else if (input.length === 6) {
      return [input.substring(0, 2), input.substring(2, 4), input.substring(4, 6)].join(_delimiter)
    }

    return input
  }
}

export const ymdToStr = (ymd: YMD, delimiter?: string) => {
  return [ymd.year, ymd.month, ymd.date].join(delimiter ?? '')
}

export const parseRemitBookInfo = (slip: RefundSlip) => {
  const isMasked = (str: string) => str.includes('*')

  if (!slip.remitBookId || slip.remitBookId === '') {
    return ''
  }

  if (isMasked(slip.remitBookId)) {
    return slip.remitBookId
  } else {
    return autoMasking(slip.remitBookId, 'center')
  }
}

export function distanceMeters (a: {lat: number; lng: number}, b: {lat: number; lng: number}): number {
  const R = 6371000 // 지구 반경(m)
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2

  return 2 * R * Math.asin(Math.sqrt(h))
}

export const getFullDateOfBirth = (dateStr: string) => {
  if (dateStr.length !== 6) return dateStr

  try {
    const yy = parseInt(dateStr.substring(0, 2))
    const mmdd = dateStr.slice(2)

    const nowYear = new Date().getFullYear()
    const thisCentury = Math.floor(nowYear / 100) * 100

    let year = thisCentury + yy

    if (year > nowYear) {
      year -= 100
    }

    return year + mmdd
  } catch (e: any) {
    console.error(e)
  }

  return dateStr
}
